import React, { useEffect, useState, useRef } from 'react';

interface Exercise {
  name: string;
  reps: number | string;
  instructions?: string;
}

interface EMOMTimerDisplayProps {
  totalMinutes: number;
  exercises: Exercise[];
  elapsedTime: number; // Total elapsed time in seconds
  onMinuteComplete?: (minute: number) => void;
}

export const EMOMTimerDisplay: React.FC<EMOMTimerDisplayProps> = ({ 
  totalMinutes, 
  exercises, 
  elapsedTime,
  onMinuteComplete 
}) => {
  const currentMinute = Math.floor(elapsedTime / 60) + 1;
  const secondsInMinute = elapsedTime % 60;
  const timeRemaining = 60 - secondsInMinute;
  const lastMinuteRef = useRef(0);
  
  // Cycle through exercises
  const currentExerciseIndex = (currentMinute - 1) % exercises.length;
  const currentExercise = exercises[currentExerciseIndex];
  const nextExerciseIndex = currentMinute % exercises.length;
  const nextExercise = exercises[nextExerciseIndex];
  
  // Audio cue at minute transitions
  useEffect(() => {
    if (currentMinute > lastMinuteRef.current && lastMinuteRef.current > 0) {
      // Play audio cue
      const audio = new Audio('/sounds/beep.mp3');
      audio.play().catch(e => console.log('Audio play failed:', e));
      
      if (onMinuteComplete) {
        onMinuteComplete(currentMinute - 1);
      }
    }
    lastMinuteRef.current = currentMinute;
  }, [currentMinute, onMinuteComplete]);
  
  // Calculate progress percentage
  const progress = (60 - timeRemaining) / 60;
  const circumference = 2 * Math.PI * 88;
  const strokeDashoffset = circumference * (1 - progress);

  return (
    <div className="flex flex-col items-center space-y-6 py-6">
      {/* Minute Counter */}
      <div className="text-center">
        <div className="text-5xl font-bold text-blue-500">
          {currentMinute} / {totalMinutes}
        </div>
        <div className="text-sm text-gray-400 mt-1">MINUTE</div>
      </div>
      
      {/* Big Circular Countdown */}
      <div className="relative flex justify-center">
        <svg className="w-48 h-48 transform -rotate-90">
          {/* Background circle */}
          <circle 
            cx="96" 
            cy="96" 
            r="88" 
            stroke="currentColor" 
            strokeWidth="12" 
            fill="none" 
            className="text-gray-700"
          />
          {/* Progress circle */}
          <circle 
            cx="96" 
            cy="96" 
            r="88" 
            stroke="currentColor" 
            strokeWidth="12" 
            fill="none" 
            className={`transition-colors ${
              timeRemaining <= 5 ? 'text-red-500' : 'text-blue-500'
            }`}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className={`text-7xl font-bold ${
            timeRemaining <= 5 ? 'text-red-500 animate-pulse' : 'text-white'
          }`}>
            {timeRemaining}
          </div>
        </div>
      </div>
      
      {/* Current Exercise */}
      <div className="text-center w-full max-w-md px-4">
        <div className="bg-gray-800 rounded-lg p-6 border-2 border-blue-500">
          <div className="text-sm text-blue-400 font-medium mb-2">CURRENT</div>
          <div className="text-2xl font-bold text-white mb-1">{currentExercise.name}</div>
          <div className="text-xl text-gray-300">{currentExercise.reps} reps</div>
          {currentExercise.instructions && (
            <div className="text-sm text-gray-400 mt-2">{currentExercise.instructions}</div>
          )}
        </div>
      </div>
      
      {/* Next Exercise Preview */}
      {currentMinute < totalMinutes && (
        <div className="text-center w-full max-w-md px-4">
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-xs text-gray-400 font-medium mb-1">NEXT</div>
            <div className="text-lg font-semibold text-gray-300">{nextExercise.name}</div>
            <div className="text-sm text-gray-400">{nextExercise.reps} reps</div>
          </div>
        </div>
      )}
      
      {/* Progress Dots */}
      <div className="flex flex-wrap justify-center gap-1 max-w-md px-4">
        {Array.from({length: totalMinutes}, (_, i) => {
          const minute = i + 1;
          return (
            <div 
              key={i}
              className={`w-2 h-2 rounded-full transition-all ${
                minute < currentMinute ? 'bg-green-500' :
                minute === currentMinute ? 'bg-blue-500 animate-pulse scale-125' :
                'bg-gray-600'
              }`}
              title={`Minute ${minute}`}
            />
          );
        })}
      </div>
      
      {/* Completion Message */}
      {currentMinute > totalMinutes && (
        <div className="text-center p-4 bg-green-900/30 border border-green-600 rounded-lg">
          <div className="text-3xl font-bold text-green-400 mb-2">🎉 EMOM Complete!</div>
          <div className="text-sm text-gray-300">
            {totalMinutes} minutes completed
          </div>
        </div>
      )}
    </div>
  );
};

